import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Collapse } from 'react-collapse';
import dynamic from 'next/dynamic';
import { LottieRefCurrentProps } from 'lottie-react';
import { Typography, getTypographyDefaultColor, Button, CheckmarkIcon } from '@ag1/ui';
import IconPlusToMinus from 'shared/ui/Icon/24/IconPlusToMinus/IconPlusToMinus';
import { ImageAndExpandableListV2, SanityKeyed } from 'types/studio/studio';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { getSanityLinkUrl } from 'shared/utils/getSanityLinkUrl';
import { useLocale } from 'shared/hooks/useLocale';
import TableSection from '../TableSection/TableSection';
import styles from './ListItem.module.scss';

const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

type ArrayElement<A> = A extends readonly (infer T)[] ? T : never;

type ListItemProps = {
  item: ArrayElement<SanityKeyed<ImageAndExpandableListV2['expandableList']>>;
  theme: ImageAndExpandableListV2['theme'];
  isOpen: boolean;
  openPopup: () => void;
  popupIsActive: boolean;
  onToggle: () => void;
};

type AnimationAsset = {
  extension: string;
  mimeType: string;
  url: string;
};

export const ListItem = ({
  item,
  isOpen,
  openPopup,
  popupIsActive,
  onToggle,
  theme
}: ListItemProps) => {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [animationData, setAnimationData] = useState(null);
  const {
    _key: key,
    headline,
    content,
    switchButton,
    button,
    linkOptions,
    tableOptions,
    animation
  } = item;

  const animationAsset: AnimationAsset = animation?.asset ?? null;
  const locale = useLocale();

  useEffect(() => {
    if (animationData || !animationAsset) return;

    if (animationAsset.extension === 'json' && animationAsset.mimeType === 'application/json') {
      fetch(animationAsset.url)
        .then((data) => data.json())
        .then((json) => {
          setAnimationData(json);
        });
    }
  }, [animationAsset, isOpen, animationData]);

  useEffect(() => {
    const currentLottieRef = lottieRef.current;
    let TimeOut: NodeJS.Timeout;

    if (isOpen && currentLottieRef) {
      TimeOut = setTimeout(() => {
        currentLottieRef.play();
      }, 500);
    }

    if (!isOpen && currentLottieRef) {
      currentLottieRef.stop();
    }

    return () => {
      clearTimeout(TimeOut);
    };
  }, [isOpen]);

  return (
    <div className={clsx(styles.listItem, isOpen && styles.active, getThemeCSSClass(theme))}>
      <div className={styles.row}>
        <span className={styles.checkIcon}>
          <CheckmarkIcon size={18} />
        </span>
        <div
          className={styles.listToggle}
          data-gtm-skip-global
          role="button"
          onClick={onToggle}
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              onToggle();
            }
          }}>
          <Typography
            id={key}
            variant="body"
            size="1"
            component="h4"
            weight="700"
            noPadding
            color={getTypographyDefaultColor(theme)}>
            {typeof headline === 'string' ? headline : <CustomPortableText value={headline} />}
          </Typography>
          <IconPlusToMinus className={clsx(styles.icon, getThemeCSSClass(theme))} />
        </div>
      </div>
      <Collapse isOpened={isOpen}>
        <div className={styles.content}>
          <Typography
            variant="body"
            size="1"
            component="div"
            noPadding
            className={styles.text}
            color={getTypographyDefaultColor(theme)}>
            <CustomPortableText value={content} />
          </Typography>
          {tableOptions?.addTable && (
            <TableSection tableOptions={tableOptions} theme={theme} insideList />
          )}
          {animationData && (
            <Lottie
              lottieRef={lottieRef}
              animationData={animationData}
              loop={false}
              autoplay={false}
              className={styles.animationContainer}
            />
          )}
          {switchButton === 'button' && button && popupIsActive && (
            <Button
              variant="link"
              type="button"
              size="small"
              rounded
              className={styles.link}
              theme={theme}
              onClick={openPopup}>
              data-gtm-skip-global
              {button}
            </Button>
          )}

          {switchButton === 'link' && linkOptions?.link && (
            <Button
              variant="link"
              href={getSanityLinkUrl(linkOptions.link, locale)}
              size="small"
              rounded
              className={styles.link}
              theme={theme}>
              {linkOptions.label}
            </Button>
          )}
        </div>
      </Collapse>
    </div>
  );
};
